export interface OrthogonalPathPoint {
  x: number;
  y: number;
}

// React Flow converts screen coordinates back to model coordinates after a
// drag or auto-layout. That conversion can leave points that should share an
// axis a fraction of a pixel apart, which must not become a real path segment.
const ORTHOGONAL_ALIGNMENT_TOLERANCE = 0.01;

function alignNearlyOrthogonalPoints(
  points: OrthogonalPathPoint[]
): OrthogonalPathPoint[] {
  const aligned = points.map((point) => ({ ...point }));
  for (let index = 1; index < aligned.length; index++) {
    const previous = aligned[index - 1]!;
    const point = aligned[index]!;
    const xDifference = Math.abs(point.x - previous.x);
    const yDifference = Math.abs(point.y - previous.y);

    if (xDifference <= ORTHOGONAL_ALIGNMENT_TOLERANCE) {
      point.x = previous.x;
    }
    if (yDifference <= ORTHOGONAL_ALIGNMENT_TOLERANCE) {
      point.y = previous.y;
    }
  }
  return aligned;
}

/** Return the rendered polyline, without duplicate or collinear waypoints. */
export function stripRedundantPoints(
  points: OrthogonalPathPoint[]
): OrthogonalPathPoint[] {
  const deduped: OrthogonalPathPoint[] = [];
  for (const point of alignNearlyOrthogonalPoints(points)) {
    const last = deduped[deduped.length - 1];
    if (!last || last.x !== point.x || last.y !== point.y) {
      deduped.push(point);
    }
  }
  const simplified: OrthogonalPathPoint[] = [];
  for (const point of deduped) {
    const a = simplified[simplified.length - 2];
    const b = simplified[simplified.length - 1];
    if (
      a &&
      b &&
      ((a.x === b.x && b.x === point.x) || (a.y === b.y && b.y === point.y))
    ) {
      simplified.pop();
    }
    simplified.push(point);
  }
  return simplified;
}

function pointToward(
  from: OrthogonalPathPoint,
  to: OrthogonalPathPoint,
  distance: number
): OrthogonalPathPoint {
  return {
    x:
      from.x +
      Math.sign(to.x - from.x) * Math.min(distance, Math.abs(to.x - from.x)),
    y:
      from.y +
      Math.sign(to.y - from.y) * Math.min(distance, Math.abs(to.y - from.y)),
  };
}

// Minimum length of the vertical segment that leaves a source handle or
// enters a target handle. A visible stub keeps the auto-oriented arrowhead
// pointed along the vertical approach instead of the horizontal rail.
const MIN_ENDPOINT_STUB = 16;

/**
 * The vertical direction the path travels at each end, expressed as a sign:
 * +1 travels downward (increasing y) out of the source handle or into the
 * target handle; -1 travels upward. Derive these from the handle sides (a
 * bottom source handle exits downward; a top target handle is entered
 * downward). When omitted, the direction falls back to the vector toward the
 * opposite handle, which is wrong for routes that wrap around a tile.
 */
export interface EndpointTravelSigns {
  source?: 1 | -1;
  target?: 1 | -1;
}

/**
 * Keep the vertical end segments of a routed path pointed at their handles.
 *
 * Stored route waypoints are computed from estimated node geometry, but the
 * first and last points of the path are the live handle positions that React
 * Flow measures after render. When the rail nearest a handle lands past the
 * measured handle, the end segment doubles back toward the handle, which
 * flips the auto-oriented arrowhead 180 degrees. When the rail lands exactly
 * on the handle, the vertical end segment collapses and the arrowhead
 * follows the horizontal rail sideways. Detect both cases and move the rail
 * so the end segment keeps a minimum-length stub in the direction of travel.
 */
export function snapEndpointRailsToHandles(
  points: OrthogonalPathPoint[],
  travelSigns?: EndpointTravelSigns,
  endpointStubs?: { source: number; target: number }
): OrthogonalPathPoint[] {
  if (points.length < 3) {
    return points;
  }
  const snapped = points.map((point) => ({ ...point }));
  // On a direct route the two stubs share the vertical travel between the
  // handles. When that travel cannot fit two full stubs, the target keeps
  // the full stub because the arrowhead renders there, and the source gets
  // the remainder.
  const travel = Math.abs(snapped[snapped.length - 1]!.y - snapped[0]!.y);
  const targetCap = Math.min(MIN_ENDPOINT_STUB, travel);
  const sourceCap = Math.min(MIN_ENDPOINT_STUB, travel - targetCap);
  // snapRailToHandle expects the direction from the handle toward its
  // adjacent rail. At the source end the path leaves the handle, so the path
  // travel sign matches. At the target end the path arrives at the handle,
  // so the adjacent rail lies opposite the travel direction.
  snapRailToHandle(
    snapped,
    0,
    1,
    sourceCap,
    travelSigns?.source,
    endpointStubs?.source
  );
  snapRailToHandle(
    snapped,
    snapped.length - 1,
    -1,
    targetCap,
    invertSign(travelSigns?.target),
    endpointStubs?.target
  );
  return snapped;
}

function invertSign(sign?: 1 | -1): 1 | -1 | undefined {
  if (sign === undefined) {
    return undefined;
  }
  return sign === 1 ? -1 : 1;
}

function snapRailToHandle(
  points: OrthogonalPathPoint[],
  handleIndex: number,
  step: 1 | -1,
  directCap: number,
  explicitTravelSign?: 1 | -1,
  endpointStub?: number
): void {
  // The handle at one end and the handle at the opposite end. Without an
  // explicit travel sign, the vector from the handle toward the opposite
  // handle approximates the intended direction of travel at this end.
  const handle = points[handleIndex]!;
  const opposite = points[handleIndex === 0 ? points.length - 1 : 0]!;
  const adjacent = points[handleIndex + step];
  if (!adjacent) {
    return;
  }
  // Only vertical entry and exit segments are snapped. The routers always
  // leave the source and enter the target vertically.
  if (Math.abs(adjacent.x - handle.x) > ORTHOGONAL_ALIGNMENT_TOLERANCE) {
    return;
  }
  const travelTowardOpposite = opposite.y - handle.y;
  const travelSign = explicitTravelSign ?? Math.sign(travelTowardOpposite);
  if (travelSign === 0) {
    return;
  }
  // When the opposite handle lies in the direction of travel (a direct
  // route), the caller caps the stub so a short direct edge cannot cross
  // the opposite handle. On a wrapped route the opposite handle lies behind
  // the travel direction, so the full stub always fits.
  const oppositeInTravelDirection = travelTowardOpposite * travelSign > 0;
  const stub =
    endpointStub ?? (oppositeInTravelDirection ? directCap : MIN_ENDPOINT_STUB);
  const segmentLength = (adjacent.y - handle.y) * travelSign;
  if (segmentLength >= stub) {
    return;
  }
  // The rail backtracks past the handle or sits too close to it. Move the
  // whole rail (the run of waypoints that share the adjacent point's y) so
  // the end segment keeps a stub in the direction of travel.
  const railY = adjacent.y;
  const snappedY = handle.y + travelSign * stub;
  for (
    let index = handleIndex + step;
    index > 0 && index < points.length - 1 && points[index]!.y === railY;
    index += step
  ) {
    points[index]!.y = snappedY;
  }
}

/** Build a rounded SVG path along an orthogonal polyline. */
export function buildRoundedOrthogonalPath(
  points: OrthogonalPathPoint[],
  borderRadius: number
): string {
  const pathPoints = stripRedundantPoints(points);
  if (pathPoints.length < 2) {
    return '';
  }

  // The first point exists because the length check above requires two points.
  let path = `M ${pathPoints[0]!.x} ${pathPoints[0]!.y}`;
  for (let index = 1; index < pathPoints.length - 1; index++) {
    const previous = pathPoints[index - 1]!;
    const corner = pathPoints[index]!;
    const next = pathPoints[index + 1]!;
    const inLength = Math.abs(
      previous.x === corner.x ? corner.y - previous.y : corner.x - previous.x
    );
    const outLength = Math.abs(
      next.x === corner.x ? next.y - corner.y : next.x - corner.x
    );
    const radius = Math.min(borderRadius, inLength / 2, outLength / 2);
    const entry = pointToward(corner, previous, radius);
    const exit = pointToward(corner, next, radius);
    path += ` L ${entry.x} ${entry.y} Q ${corner.x} ${corner.y} ${exit.x} ${exit.y}`;
  }
  const last = pathPoints[pathPoints.length - 1]!;
  return `${path} L ${last.x} ${last.y}`;
}

/** Return the midpoint of the longest segment as a stable label position. */
export function getOrthogonalPathLabelPoint(
  points: OrthogonalPathPoint[]
): OrthogonalPathPoint {
  const pathPoints = stripRedundantPoints(points);
  if (pathPoints.length === 0) {
    return { x: 0, y: 0 };
  }
  if (pathPoints.length === 1) {
    return pathPoints[0]!;
  }
  let bestStart = pathPoints[0]!;
  let bestEnd = pathPoints[1]!;
  let bestLength = 0;
  for (let index = 1; index < pathPoints.length; index++) {
    const start = pathPoints[index - 1]!;
    const end = pathPoints[index]!;
    const length = Math.abs(end.x - start.x) + Math.abs(end.y - start.y);
    if (length > bestLength) {
      bestLength = length;
      bestStart = start;
      bestEnd = end;
    }
  }
  return {
    x: (bestStart.x + bestEnd.x) / 2,
    y: (bestStart.y + bestEnd.y) / 2,
  };
}
